import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Navigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { validate as uuidValidate } from 'uuid';
import { UserService } from '../../../services';
import LoadingProgress from '../../../layout/LoadingProgress';
import { useConfig } from '../../../config';
import { useQuery } from '../../../routes';

const useStyles = makeStyles(theme => ({
  textField: {
    marginTop: theme.spacing(2),
    minWidth: 300,
  },
  error: {
    color: theme.palette.error.main,
    margin: theme.spacing(2),
  },
  title: {
    textAlign: 'center',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '.2rem',
  },
  errorIcon: {
    marginRight: '.5rem',
  },
}));

interface ResetPasswordInput {
  password: string;
  confirmPassword: string;
}

const ResetPassword = () => {
  const classes = useStyles();
  const { queryString, clear } = useQuery();
  const { search } = useLocation();
  const { config } = useConfig();
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  const {
    register,
    handleSubmit,
    formState: { errors: inputErrors },
    watch,
  } = useForm<ResetPasswordInput>({
    defaultValues: { password: '', confirmPassword: '' },
  });
  const [open, setOpen] = useState(false);
  const [successfulReset, setSuccessfulReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(['']);
  const { enqueueSnackbar } = useSnackbar();
  const handleClose = () => setOpen(false);
  const RESET_CODE_PARAM = 'reset_password_code';

  const resetPasswordCode = new URLSearchParams(search).get(RESET_CODE_PARAM);

  useEffect(() => {
    if (resetPasswordCode && !uuidValidate(resetPasswordCode)) {
      enqueueSnackbar(<FormattedMessage id="reset.form.unvalid_link" />, { variant: 'error' });
      handleClose();
    } else {
      setOpen(!!resetPasswordCode);
    }
  }, [resetPasswordCode, enqueueSnackbar]);

  const doResetPassword = async (input: ResetPasswordInput) => {
    if (resetPasswordCode) {
      setLoading(true);
      try {
        await (UserService.resetPassword({ password: input.password, resetPasswordCode }));
        analytics.track(
          'User Changed Password',
          {
            event_text: 'User Changed Password',
          },
        );
        setSuccessfulReset(true);
        enqueueSnackbar(<FormattedMessage id="reset.form.resetsuccess" />, { variant: 'success' });
      } catch (e: any) {
        const responseData = e.response && e.response.data && e.response.data.errors;
        setErrors(responseData);
        if (!(responseData && responseData.includes('password_match_email'))) {
          enqueueSnackbar(<FormattedMessage id="reset.form.expired" />, { variant: 'error' });
          handleClose();
        }
      } finally {
        setLoading(false);
      }
    }
  };

  if (successfulReset) {
    if (config?.onpremise) {
      clear(RESET_CODE_PARAM);
      return (
        <Navigate
          to={{
            pathname: '/login',
            search: queryString(),
          }}
          replace
        />
      );
    }
    return <Navigate to="/" replace />;
  }

  return (
    <Dialog fullScreen={fullScreen} open={open} aria-labelledby="form-dialog-title">
      <DialogTitle className={classes.title}>
        <FormattedMessage id="reset.form.title" values={{ char: '' }} />
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(doResetPassword)} id="reset-password-form">
          <DialogContentText>
            <FormattedMessage id="reset.form.instruction" />
          </DialogContentText>
          <TextField
            variant="outlined"
            fullWidth
            type="password"
            autoFocus
            name="password"
            label={<FormattedMessage id="reset.form.title" values={{ char: '*' }} />}
            className={classes.textField}
            inputProps={{
              ...register('password', {
                required: 'error.field.required',
                pattern: {
                  value:
                    /^(?=.*[a-zA-Z]?)(?=.*[@#$%^&+=!*\\/?`(){}[\]|;:'",.<>])(?=.*\d?).{8,255}$/,
                  message: 'error.user.password.invalid',
                },
              }),
            }}
            helperText={
              inputErrors.password
              && (
                <div className={classes.errorContainer}>
                  <WarningIcon className={classes.errorIcon} />
                  <FormattedMessage
                    id={inputErrors.password?.message}
                    values={{ field: <FormattedMessage id="reset.form.title" values={{ char: '' }} /> }}
                  />
                </div>
              )
            }
            error={!!inputErrors.password}
          />
          {errors
            && errors.includes('password_match_email')
            && <Typography variant="caption" className={classes.error}><FormattedMessage id="error.passwordmatchemail" /></Typography>}
          <TextField
            variant="outlined"
            fullWidth
            type="password"
            name="confirmPassword"
            label={<FormattedMessage id="reset.form.confirm" values={{ char: '*' }} />}
            className={classes.textField}
            inputProps={{
              ...register('confirmPassword', {
                required: 'error.field.required',
                validate: value => value === watch('password') || 'error.confirm_password_does_not_match',
              }),
            }}
            helperText={
              inputErrors.confirmPassword
              && (
                <div className={classes.errorContainer}>
                  <WarningIcon className={classes.errorIcon} />
                  <FormattedMessage
                    id={inputErrors.confirmPassword?.message}
                    values={{ field: <FormattedMessage id="reset.form.confirm" values={{ char: '' }} /> }}
                  />
                </div>
              )
            }
            error={!!inputErrors.confirmPassword}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={handleClose}>
          <FormattedMessage id="reset.form.cancel" />
        </Button>
        <Button type="submit" form="reset-password-form" color="primary">
          <FormattedMessage id="reset.form.submit" />
        </Button>
      </DialogActions>
      {loading && <LoadingProgress />}
    </Dialog>
  );
};

export default ResetPassword;
